import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    username: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    // Password validation - strong requirements only for registration
    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    } else if (!isLogin) {
      // Strong password only for registration
      if (formData.password.length < 8) {
        newErrors.password = 'Senha deve ter no mínimo 8 caracteres';
      } else if (!/[A-Z]/.test(formData.password)) {
        newErrors.password = 'Senha deve conter uma letra maiúscula';
      } else if (!/[a-z]/.test(formData.password)) {
        newErrors.password = 'Senha deve conter uma letra minúscula';
      } else if (!/[0-9]/.test(formData.password)) {
        newErrors.password = 'Senha deve conter um número';
      } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
        newErrors.password = 'Senha deve conter um caractere especial';
      }
    }

    // Username validation (only for register)
    if (!isLogin) {
      if (!formData.username.trim()) {
        newErrors.username = 'Usuário é obrigatório';
      } else if (formData.username.length < 3) {
        newErrors.username = 'Usuário deve ter no mínimo 3 caracteres';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await register(formData.username, formData.email, formData.password);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao processar solicitação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />
      <style>{`
        .lg *, .lg *::before, .lg *::after { margin: 0; padding: 0; box-sizing: border-box; }
        .lg { font-family: 'Lexend', sans-serif; color: #555; min-height: 100vh; overflow-x: hidden; -webkit-font-smoothing: antialiased; }
        
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        
        .lg-container {
          min-height: 100vh; background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f172a 100%);
          display: flex; align-items: center; justify-content: center; padding: 20px; position: relative; overflow: hidden;
        }
        .lg-bg-blob {
          position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.3;
          animation: blob 7s infinite;
        }
        .lg-bg-blob:nth-child(1) { width: 300px; height: 300px; background: #f90; top: -100px; right: -100px; }
        .lg-bg-blob:nth-child(2) { width: 300px; height: 300px; background: #ff6600; bottom: -100px; left: -100px; animation-delay: 2s; }
        .lg-bg-blob:nth-child(3) { width: 250px; height: 250px; background: #f90; top: 50%; left: 50%; transform: translate(-50%, -50%); animation-delay: 4s; }
        
        .lg-card {
          background: rgba(255,255,255,0.98); border-radius: 24px; padding: 40px; width: 100%; max-width: 420px;
          box-shadow: 0 25px 80px rgba(0,0,0,0.4); position: relative; z-index: 10;
          animation: fadeIn 0.6s ease-out; border: 1px solid rgba(255,153,0,0.1);
        }
        .lg-card:hover { transform: scale(1.01); transition: transform 0.3s ease; }
        
        .lg-header { text-align: center; margin-bottom: 30px; }
        .lg-logo { width: 90px; height: 90px; margin: 0 auto 16px; }
        .lg-logo img { width: 100%; height: 100%; object-fit: contain; }
        .lg-title { font-size: 32px; font-weight: 800; color: #1a1a2e; letter-spacing: 1px; }
        .lg-subtitle { color: #666; margin-top: 6px; font-size: 15px; font-weight: 500; }
        
        .lg-error { 
          background: rgba(229,62,62,0.1); border-left: 4px solid #e53e3e; color: #e53e3e;
          padding: 12px 16px; border-radius: 0 10px 10px 0; margin-bottom: 20px; font-size: 14px;
          animation: shake 0.5s ease-in-out;
        }
        
        .lg-form-group { margin-bottom: 20px; }
        .lg-label { display: block; font-size: 13px; font-weight: 600; color: #444; margin-bottom: 8px; }
        .lg-input-wrap { position: relative; }
        .lg-input-icon { 
          position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
          color: #f90; font-size: 16px; transition: all 0.3s;
        }
        .lg-input {
          width: 100%; padding: 14px 14px 14px 42px; border: 2px solid #e0e0e0; border-radius: 12px;
          font-size: 15px; outline: none; transition: all 0.3s; font-family: 'Lexend', sans-serif;
        }
        .lg-input:focus { border-color: #f90; box-shadow: 0 0 0 3px rgba(255,153,0,0.15); }
        .lg-input:focus + .lg-input-icon, .lg-input-wrap:focus-within .lg-input-icon { color: #f90; }
        .lg-input.error { border-color: #e53e3e; }
        .lg-input.error:focus { box-shadow: 0 0 0 3px rgba(229,62,62,0.15); }
        .lg-error-text { color: #e53e3e; font-size: 12px; margin-top: 6px; }
        .lg-hint { color: #888; font-size: 12px; margin-top: 6px; }
        
        .lg-btn {
          width: 100%; background: linear-gradient(135deg, #f90, #ff6600); color: #fff;
          padding: 16px; border-radius: 12px; border: none; cursor: pointer; font-weight: 700;
          font-size: 17px; transition: all 0.3s; box-shadow: 0 4px 15px rgba(255,153,0,0.3);
          display: flex; align-items: center; justify-content: center; gap: 10px;
        }
        .lg-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(255,153,0,0.4); }
        .lg-btn:disabled { background: #999; cursor: not-allowed; box-shadow: none; transform: none; }
        .lg-spinner { width: 20px; height: 20px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        
        .lg-link-track { 
          display: block; text-align: center; margin-top: 20px; color: #f90;
          font-weight: 600; font-size: 14px; text-decoration: none; transition: all 0.3s;
        }
        .lg-link-track:hover { color: #ff6600; text-decoration: underline; }
        .lg-link-track i { margin-right: 6px; }
        
        .lg-toggle { 
          display: block; width: 100%; text-align: center; margin-top: 20px;
          background: none; border: none; color: #666; font-weight: 600; cursor: pointer;
          font-size: 14px; transition: all 0.3s; font-family: 'Lexend', sans-serif;
        }
        .lg-toggle:hover { color: #f90; text-decoration: underline; }
        
        @media (max-width: 480px) {
          .lg-card { padding: 30px 24px; border-radius: 20px; }
          .lg-title { font-size: 28px; }
          .lg-logo { width: 70px; height: 70px; }
        }
      `}</style>

      <div className="lg">
        <div className="lg-container">
          {/* Background blobs */}
          <div className="lg-bg-blob"></div>
          <div className="lg-bg-blob"></div>
          <div className="lg-bg-blob"></div>

          <div className="lg-card">
            <div className="lg-header">
              <h1 className="lg-title">COMEXTRACKING</h1>
              <p className="lg-subtitle">Sistema de Rastreamentos</p>
            </div>

            {error && <div className="lg-error">{error}</div>}

            <form onSubmit={handleSubmit}>
              {/* Honeypot */}
              <input type="text" name="website" style={{ display: 'none' }} tabIndex={-1} autoComplete="off" />

              {!isLogin && (
                <div className="lg-form-group">
                  <label className="lg-label">Usuário</label>
                  <div className="lg-input-wrap">
                    <i className="fas fa-user lg-input-icon"></i>
                    <input
                      type="text" required value={formData.username}
                      onChange={(e) => { setFormData({ ...formData, username: e.target.value }); if (errors.username) setErrors({ ...errors, username: '' }); }}
                      className={`lg-input ${errors.username ? 'error' : ''}`} placeholder="Seu usuário"
                    />
                  </div>
                  {errors.username && <p className="lg-error-text">{errors.username}</p>}
                </div>
              )}

              <div className="lg-form-group">
                <label className="lg-label">Email</label>
                <div className="lg-input-wrap">
                  <i className="fas fa-envelope lg-input-icon"></i>
                  <input
                    type="email" required value={formData.email}
                    onChange={(e) => { setFormData({ ...formData, email: e.target.value }); if (errors.email) setErrors({ ...errors, email: '' }); }}
                    className={`lg-input ${errors.email ? 'error' : ''}`} placeholder="seu@email.com"
                  />
                </div>
                {errors.email && <p className="lg-error-text">{errors.email}</p>}
              </div>

              <div className="lg-form-group">
                <label className="lg-label">Senha</label>
                <div className="lg-input-wrap">
                  <i className="fas fa-lock lg-input-icon"></i>
                  <input
                    type="password" required value={formData.password}
                    onChange={(e) => { setFormData({ ...formData, password: e.target.value }); if (errors.password) setErrors({ ...errors, password: '' }); }}
                    className={`lg-input ${errors.password ? 'error' : ''}`} placeholder="••••••••"
                  />
                </div>
                {errors.password && <p className="lg-error-text">{errors.password}</p>}
                {!isLogin && <p className="lg-hint">Mínimo 8 caracteres, maiúscula, minúscula, número e especial</p>}
              </div>

              <button type="submit" disabled={loading} className="lg-btn">
                {loading ? <><div className="lg-spinner"></div> Processando...</> : isLogin ? 'Entrar' : 'Cadastrar'}
              </button>
            </form>

            <Link to="/track" className="lg-link-track"><i className="fas fa-search"></i> Rastrear meu pedido</Link>

            <button onClick={() => { setIsLogin(!isLogin); setError(''); }} className="lg-toggle">
              {isLogin ? 'Não tem conta? Cadastre-se' : 'Já tem conta? Faça login'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
